import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Question } from '../../types';
import { X, Wallet, RotateCcw } from 'lucide-react';
interface MoneyAssemblerProps {
  question: Question;
  onComplete: (userAnswer: string) => void;
  onReadyChange?: (isReady: boolean) => void;
  shouldValidate?: boolean;
}
export function MoneyAssembler({
  question,
  onComplete,
  onReadyChange,
  shouldValidate = false
}: MoneyAssemblerProps) {
  const availableDenominations = question.options.map(Number);
  const [selectedCoins, setSelectedCoins] = useState<number[]>([]);
  const isReady = selectedCoins.length > 0;
  useEffect(() => {
    onReadyChange?.(isReady);
  }, [isReady, onReadyChange]);
  useEffect(() => {
    if (shouldValidate) {
      const userAnswer = selectedCoins.join(',');
      onComplete(userAnswer);
    }
  }, [shouldValidate, selectedCoins, onComplete]);
  // Reset when question changes
  useEffect(() => {
    setSelectedCoins([]);
  }, [question.id]);
  const handleAddCoin = (denomination: number) => {
    setSelectedCoins([...selectedCoins, denomination]);
  };
  const handleRemoveCoin = (index: number) => {
    setSelectedCoins(selectedCoins.filter((_, i) => i !== index));
  };
  const handleReset = () => {
    setSelectedCoins([]);
  };
  const getDenominationDisplay = (cents: number): string => {
    if (cents >= 100) return `€${cents / 100}`;
    return `${cents}`;
  };
  const isBill = (cents: number) => cents >= 500;
  const getMoneyStyles = (cents: number) => {
    if (cents === 5000) {
      return {
        container: 'w-24 h-14 md:w-28 md:h-16 rounded-md shadow-md',
        bg: 'bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 border-orange-700',
        text: 'text-white font-bold text-lg md:text-xl',
        border: 'border-2 border-opacity-50',
        inner: 'bg-white/10'
      };
    }
    if (cents === 2000) {
      return {
        container: 'w-22 h-13 md:w-26 md:h-15 rounded-md shadow-md',
        bg: 'bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600 border-blue-700',
        text: 'text-white font-bold text-lg md:text-xl',
        border: 'border-2 border-opacity-50',
        inner: 'bg-white/10'
      };
    }
    if (cents === 1000) {
      return {
        container: 'w-20 h-12 md:w-24 md:h-14 rounded-md shadow-md',
        bg: 'bg-gradient-to-br from-rose-400 via-rose-500 to-rose-600 border-rose-700',
        text: 'text-white font-bold text-lg md:text-xl',
        border: 'border-2 border-opacity-50',
        inner: 'bg-white/10'
      };
    }
    if (cents === 500) {
      return {
        container: 'w-18 h-11 md:w-22 md:h-13 rounded-md shadow-md',
        bg: 'bg-gradient-to-br from-slate-400 via-slate-500 to-slate-600 border-slate-700',
        text: 'text-white font-bold text-lg md:text-xl',
        border: 'border-2 border-opacity-50',
        inner: 'bg-white/10'
      };
    }
    if (cents === 200) {
      return {
        container: 'w-14 h-14 md:w-16 md:h-16 rounded-full shadow-lg',
        bg: 'bg-gradient-to-br from-slate-300 via-slate-100 to-slate-400 border-slate-400',
        text: 'text-slate-800 font-bold text-base md:text-lg',
        border: 'border-2',
        inner: 'bg-gradient-to-br from-amber-300 via-yellow-200 to-amber-500 rounded-full w-8 h-8 md:w-10 md:h-10 flex items-center justify-center shadow-inner border border-amber-600/30'
      };
    }
    if (cents === 100) {
      return {
        container: 'w-12 h-12 md:w-14 md:h-14 rounded-full shadow-lg',
        bg: 'bg-gradient-to-br from-amber-300 via-yellow-200 to-amber-500 border-amber-600',
        text: 'text-amber-900 font-bold text-base md:text-lg',
        border: 'border-2',
        inner: 'bg-gradient-to-br from-slate-300 via-slate-100 to-slate-400 rounded-full w-7 h-7 md:w-9 md:h-9 flex items-center justify-center shadow-inner border border-slate-400/30'
      };
    }
    if (cents === 50) {
      return {
        container: 'w-12 h-12 md:w-14 md:h-14 rounded-full shadow-md',
        bg: 'bg-gradient-to-br from-yellow-300 via-yellow-100 to-yellow-500 border-yellow-600',
        text: 'text-yellow-900 font-bold text-base md:text-lg',
        border: 'border-2',
        inner: 'w-full h-full flex items-center justify-center'
      };
    }
    if (cents === 20) {
      return {
        container: 'w-10 h-10 md:w-12 md:h-12 rounded-full shadow-md',
        bg: 'bg-gradient-to-br from-yellow-300 via-yellow-100 to-yellow-500 border-yellow-600',
        text: 'text-yellow-900 font-bold text-sm md:text-base',
        border: 'border-2',
        inner: 'w-full h-full flex items-center justify-center'
      };
    }
    if (cents === 10) {
      return {
        container: 'w-8 h-8 md:w-10 md:h-10 rounded-full shadow-md',
        bg: 'bg-gradient-to-br from-yellow-300 via-yellow-100 to-yellow-500 border-yellow-600',
        text: 'text-yellow-900 font-bold text-xs md:text-sm',
        border: 'border-2',
        inner: 'w-full h-full flex items-center justify-center'
      };
    }
    if (cents <= 5) {
      return {
        container: 'w-7 h-7 md:w-9 md:h-9 rounded-full shadow-sm',
        bg: 'bg-gradient-to-br from-orange-300 via-orange-200 to-orange-500 border-orange-600',
        text: 'text-orange-900 font-bold text-[10px] md:text-xs',
        border: 'border-2',
        inner: 'w-full h-full flex items-center justify-center'
      };
    }
    return {
      container: 'w-12 h-12 rounded-full',
      bg: 'bg-gray-200',
      text: 'text-gray-800',
      border: 'border',
      inner: ''
    };
  };
  const MoneyItem = ({
    cents,
    onClick,
    disabled = false,
    small = false
  }: {
    cents: number;
    onClick?: () => void;
    disabled?: boolean;
    small?: boolean;
  }) => {
    const styles = getMoneyStyles(cents);
    const isBillItem = isBill(cents);
    const scaleClass = small ? 'scale-75 origin-center' : '';
    return <motion.button whileHover={!disabled && !small ? {
      scale: 1.05,
      rotate: 1,
      y: -2
    } : {}} whileTap={!disabled && !small ? {
      scale: 0.95
    } : {}} onClick={onClick} disabled={disabled} className={`
          relative flex items-center justify-center
          ${styles.container} ${styles.bg} ${styles.border}
          ${scaleClass}
          transition-shadow duration-200
          ${!disabled && !small ? 'hover:shadow-lg cursor-pointer' : ''}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `} aria-label={getDenominationDisplay(cents)} data-id="element-1286">

        <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/20 to-white/0 rounded-[inherit] pointer-events-none" data-id="element-1287" />
        <div className={`${styles.inner} relative z-10`} data-id="element-1288">
          <span className={`${styles.text} drop-shadow-sm`} data-id="element-1289">
            {getDenominationDisplay(cents)}
            {cents < 100 && <span className="text-[0.6em] ml-0.5" data-id="element-1290">c</span>}
          </span>
        </div>
        {isBillItem && <div className="absolute right-3 top-0 bottom-0 w-1.5 bg-white/10 border-l border-r border-white/20" data-id="element-1291" />}
      </motion.button>;
  };
  return <div className="w-full max-w-3xl mx-auto space-y-6" data-id="element-1292">
      {/* Step 1: Selection */}
      <div className="space-y-3" data-id="element-1293">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2" data-id="element-1294">
          <span className="w-5 h-5 rounded-full bg-indigo-500 text-white flex items-center justify-center text-[10px]" data-id="element-1295">
            1
          </span>
          Kies munten en biljetten
        </h3>

        <div className="bg-white rounded-2xl p-4 md:p-6 border border-slate-200 shadow-sm" data-id="element-1296">
          <div className="flex flex-wrap gap-3 md:gap-4 justify-center items-center" data-id="element-1297">
            {availableDenominations.map((denomination, index) => <motion.div key={`denom-${denomination}`} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.03
          }} data-id="element-1298">

                <MoneyItem cents={denomination} onClick={() => handleAddCoin(denomination)} data-id="element-1299" />

              </motion.div>)}
          </div>
        </div>
      </div>

      {/* Step 2: Wallet */}
      <div className="space-y-3" data-id="element-1300">
        <div className="flex items-center justify-between" data-id="element-1301">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2" data-id="element-1302">
            <span className="w-5 h-5 rounded-full bg-indigo-500 text-white flex items-center justify-center text-[10px]" data-id="element-1303">
              2
            </span>
            Jouw portemonnee
          </h3>

          {/* Reset Button */}
          {selectedCoins.length > 0 && <motion.button initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} onClick={handleReset} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors" data-id="element-1304">

              <RotateCcw className="w-3.5 h-3.5" data-id="element-1305" />
              Opnieuw
            </motion.button>}
        </div>

        <div className="relative min-h-[140px] bg-slate-50 rounded-2xl border-2 border-dashed border-slate-300 overflow-hidden" data-id="element-1306">
          {selectedCoins.length === 0 && <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 pointer-events-none" data-id="element-1307">
              <Wallet className="w-10 h-10 mb-2 opacity-30" data-id="element-1308" />
              <p className="text-sm font-medium" data-id="element-1309">De portemonnee is nog leeg</p>
            </div>}

          <div className="relative z-10 p-4 flex flex-wrap content-start gap-2" data-id="element-1310">
            <AnimatePresence mode="popLayout" data-id="element-1311">
              {selectedCoins.map((coin, index) => <motion.div key={`selected-${index}-${coin}`} layout initial={{
              scale: 0,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} exit={{
              scale: 0,
              opacity: 0
            }} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} className="relative group/coin" data-id="element-1312">

                  <MoneyItem cents={coin} small data-id="element-1313" />
                  <button onClick={() => handleRemoveCoin(index)} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md opacity-100 md:opacity-0 md:group-hover/coin:opacity-100 transition-opacity" aria-label="Verwijder" data-id="element-1314">

                    <X className="w-3 h-3" data-id="element-1315" />
                  </button>
                </motion.div>)}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>;
}